import React, { useState } from 'react';
import { Contact } from '@aws-sdk/client-connect';
import { DetailField } from './detail-field';
import { DetailSection } from './detail-section';

import { Input } from '@@/ui/primitives';
import clsx from 'clsx';

export const ContactDetails = (props: { contact?: Contact }) => {
  const [searchTerm, setSearchTerm] = useState('');

  if (!props.contact) {
    return (
      <div className='flex items-center justify-center h-full text-neutral-500'>No contact information available</div>
    );
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  const matchesSearch = (label: string, value: unknown): boolean => {
    if (!normalizedSearchTerm) return true;

    const labelMatches = label.toLowerCase().includes(normalizedSearchTerm);

    if (value === null || value === undefined) {
      return labelMatches;
    }

    let valueString = '';
    if (typeof value === 'object') {
      valueString = JSON.stringify(value).toLowerCase();
    } else {
      valueString = String(value).toLowerCase();
    }

    const valueMatches = valueString.includes(normalizedSearchTerm);

    return labelMatches || valueMatches;
  };

  const filterDetailFields = (fields: Array<{ label: string; value: unknown; isDate?: boolean }>) => {
    return fields.filter((field) => matchesSearch(field.label, field.value));
  };

  const attributeKeyValuePairs = props.contact.Attributes
    ? Object.entries(props.contact.Attributes)
        .filter(([key, value]) => matchesSearch(key, value))
        .map(([key, value]) => <DetailField key={key} label={key} value={value} />)
    : [<DetailField key='none' label='Attributes' value='No attributes available' />];

  return (
    <div className='h-full flex flex-col flex-1'>
      <div className='flex flex-col border-b border-neutral-200 bg-neutral-100 h-full'>
        <div className='flex items-center justify-between bg-white'>
          <div className='flex items-center gap-1 h-10 flex-1'>
            <Input
              placeholder='Search details...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={clsx(
                'shadow-none rounded-sm text-sm! bg-transparent border-0!',
                'ring-0! focus:shadow-none',
                'text-neutral-950 placeholder:text-neutral-400',
                'outline-0!',
                'w-full'
              )}
            />
          </div>
        </div>
        <div className='flex-1 min-h-0'>
          <div className='max-h-full overflow-y-auto'>
            {(() => {
              const basicFields = filterDetailFields([
                { label: 'Contact ID', value: props.contact.Id },
                { label: 'ARN', value: props.contact.Arn },
                { label: 'Name', value: props.contact.Name },
                { label: 'Description', value: props.contact.Description },
                { label: 'Channel', value: props.contact.Channel }
              ]);

              const customerEndpointFields = filterDetailFields([
                { label: 'Type', value: props.contact.CustomerEndpoint?.Type },
                { label: 'Address', value: props.contact.CustomerEndpoint?.Address },
                { label: 'Display Name', value: props.contact.CustomerEndpoint?.DisplayName }
              ]);

              const systemEndpointFields = filterDetailFields([
                { label: 'Type', value: props.contact.SystemEndpoint?.Type },
                { label: 'Address', value: props.contact.SystemEndpoint?.Address },
                { label: 'Display Name', value: props.contact.SystemEndpoint?.DisplayName }
              ]);

              const additionalFields = filterDetailFields([
                { label: 'Quality Metrics', value: props.contact.QualityMetrics },
                { label: 'Wisdom Info', value: props.contact.WisdomInfo },
                { label: 'Disconnect Details', value: props.contact.DisconnectDetails },
                { label: 'Segment Attributes', value: props.contact.SegmentAttributes },
                { label: 'Tags', value: props.contact.Tags }
              ]);

              const timestampFields = filterDetailFields([
                { label: 'Initiation Timestamp', value: props.contact.InitiationTimestamp, isDate: true },
                { label: 'Connected To System', value: props.contact.ConnectedToSystemTimestamp, isDate: true },
                { label: 'Scheduled Timestamp', value: props.contact.ScheduledTimestamp, isDate: true },
                { label: 'Last Update Timestamp', value: props.contact.LastUpdateTimestamp, isDate: true },
                { label: 'Last Paused Timestamp', value: props.contact.LastPausedTimestamp, isDate: true },
                { label: 'Last Resumed Timestamp', value: props.contact.LastResumedTimestamp, isDate: true },
                { label: 'Disconnect Timestamp', value: props.contact.DisconnectTimestamp, isDate: true },
                { label: 'Total Pause Duration (seconds)', value: props.contact.TotalPauseDurationInSeconds }
              ]);

              const queueFields = filterDetailFields([
                { label: 'Queue Info', value: props.contact.QueueInfo },
                { label: 'Queue Priority', value: props.contact.QueuePriority },
                { label: 'Queue Time Adjustment (seconds)', value: props.contact.QueueTimeAdjustmentSeconds },
                { label: 'Routing Criteria', value: props.contact.RoutingCriteria }
              ]);

              const agentFields = filterDetailFields([
                { label: 'Agent Info', value: props.contact.AgentInfo },
                { label: 'Customer', value: props.contact.Customer },
                { label: 'Customer Voice Activity', value: props.contact.CustomerVoiceActivity }
              ]);

              const relatedFields = filterDetailFields([
                { label: 'Previous Contact ID', value: props.contact.PreviousContactId },
                { label: 'Initial Contact ID', value: props.contact.InitialContactId },
                { label: 'Related Contact ID', value: props.contact.RelatedContactId }
              ]);

              const campaignFields = filterDetailFields([
                { label: 'Campaign', value: props.contact.Campaign },
                { label: 'Answering Machine Detection', value: props.contact.AnsweringMachineDetectionStatus }
              ]);

              return (
                <>
                  {basicFields.length > 0 && (
                    <DetailSection title='Basic Information'>
                      {basicFields.map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} isDate={field.isDate} />
                      ))}
                    </DetailSection>
                  )}

                  <DetailSection title='User Defined Attributes'>{attributeKeyValuePairs}</DetailSection>

                  {customerEndpointFields.length > 0 && (
                    <DetailSection title='Customer Endpoint'>
                      {customerEndpointFields.map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} isDate={field.isDate} />
                      ))}
                    </DetailSection>
                  )}

                  {systemEndpointFields.length > 0 && (
                    <DetailSection title='System Endpoint'>
                      {systemEndpointFields.map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} isDate={field.isDate} />
                      ))}
                    </DetailSection>
                  )}

                  {additionalFields.length > 0 && (
                    <DetailSection title='Additional Information'>
                      {additionalFields.map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} isDate={field.isDate} />
                      ))}
                    </DetailSection>
                  )}

                  {timestampFields.length > 0 && (
                    <DetailSection title='Timestamps & Duration'>
                      {timestampFields.map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} isDate={field.isDate} />
                      ))}
                    </DetailSection>
                  )}

                  {queueFields.length > 0 && (
                    <DetailSection title='Queue & Routing'>
                      {queueFields.map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} isDate={field.isDate} />
                      ))}
                    </DetailSection>
                  )}

                  {agentFields.length > 0 && (
                    <DetailSection title='Agent & Customer'>
                      {agentFields.map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} isDate={field.isDate} />
                      ))}
                    </DetailSection>
                  )}

                  {relatedFields.length > 0 && (
                    <DetailSection title='Related Contacts'>
                      {relatedFields.map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} isDate={field.isDate} />
                      ))}
                    </DetailSection>
                  )}

                  {campaignFields.length > 0 && (
                    <DetailSection title='Campaign & Detection'>
                      {campaignFields.map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} isDate={field.isDate} />
                      ))}
                    </DetailSection>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};
